import React from 'react';
import {
    Caption,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import * as S from './InformationTab.style';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { useLoaderData } from '@remix-run/react';

interface LibraryTab {
    ID: number;
    Title: string;
    Content?: string | null;
    MemberSortOrder: number;
}

interface InformationTabData {
    SpecificationTab: LibraryTab | undefined;
    PerformanceDescription: string;
    ScopeOfUseDescription: string;
    LibraryTabs: LibraryTab[];
}

interface InformationTabProps {
    data: InformationTabData;
}

interface InformationPanel {
    key: string;
    title: string;
    content: string;
}

const InformationTab = ({ data }: InformationTabProps) => {
    const { css } = useStyles();
    const {
        SpecificationTab,
        ScopeOfUseDescription,
        PerformanceDescription,
        LibraryTabs,
    } = data;
    const informationSections: InformationPanel[] = [];
    const { mobile } = useAppDisplayModeContext();
    const theme = useTheme();
    const { product } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: product.Professional.ID,
        professionalTitle: product.Professional.Title,
        itemType: 'Product',
    });

    if (SpecificationTab) {
        informationSections.push({
            key: 'specifications',
            title: 'Specifications',
            content: SpecificationTab.Content || '',
        });
    }

    if (ScopeOfUseDescription) {
        informationSections.push({
            key: 'scope-of-use',
            title: 'Scope of use',
            content: ScopeOfUseDescription,
        });
    }
    if (PerformanceDescription) {
        informationSections.push({
            key: 'features',
            title: 'Features',
            content: PerformanceDescription,
        });
    }

    if (LibraryTabs.length) {
        LibraryTabs.sort(
            (a, b) => b.MemberSortOrder - a.MemberSortOrder
        ).forEach(({ Title, Content }, i) => {
            if (Content) {
                informationSections.push({
                    key: `custom-panel-${i}`,
                    title: Title,
                    content: Content,
                });
            }
        });
    }

    return (
        <div className={css(S.TabWrapper)}>
            {informationSections.map((section) => (
                <div key={section.key}>
                    <Caption
                        className={css(S.SectionHeading)}
                        variant="02"
                        variables={{
                            fontSize: mobile
                                ? theme.siteVariables.fontSizes.caption04
                                : theme.siteVariables.fontSizes.caption03,
                        }}
                    >
                        {section.title}
                    </Caption>
                    <div
                        className={css(S.SectionContent)}
                        dangerouslySetInnerHTML={{
                            __html: section.content,
                        }}
                        onClick={clickProxy}
                    />
                </div>
            ))}
        </div>
    );
};

export default InformationTab;
